<template>
    <v-row justify="center">
        <v-dialog v-model="show_location_guides_dialog" max-width="1800" persistent scrollable>
            <v-card>
                <v-card-title>Location Guides</v-card-title>
                <v-card-subtitle class="pt-1">
                    Survival and pub crawl guides. Organisers and invitees see the entries for their event's location
                    in the client portal.
                </v-card-subtitle>
                <v-card-text style="height:650px;">
                    <v-row>
                        <v-col cols="3" class="pr-6">
                            <v-text-field v-model="location_search" label="Search locations" prepend-inner-icon="mdi-magnify"
                                outlined dense clearable hide-details class="mb-3"></v-text-field>
                            <v-list dense style="height:560px;overflow-y:auto;">
                                <v-list-item-group v-model="selected_location_id" mandatory>
                                    <v-list-item v-for="location in filtered_locations" :key="location.id" :value="location.id">
                                        <v-list-item-content>
                                            <v-list-item-title>{{ location.name }}</v-list-item-title>
                                        </v-list-item-content>
                                        <v-list-item-action-text>
                                            <span v-if="countFor(location.id, 'survival')" class="mr-2"
                                                title="Survival entries">
                                                <v-icon x-small>mdi-lifebuoy</v-icon> {{ countFor(location.id, 'survival') }}
                                            </span>
                                            <span v-if="countFor(location.id, 'pub_crawl')" title="Pub crawl entries">
                                                <v-icon x-small>mdi-glass-mug-variant</v-icon> {{ countFor(location.id, 'pub_crawl') }}
                                            </span>
                                        </v-list-item-action-text>
                                    </v-list-item>
                                </v-list-item-group>
                            </v-list>
                        </v-col>
                        <v-col cols="9" class="pl-6">
                            <div v-if="!selected_location" class="mt-10 text-center grey--text">Choose a location</div>
                            <template v-else>
                                <div class="d-flex align-center mb-3">
                                    <v-btn-toggle v-model="selected_type" mandatory dense>
                                        <v-btn value="survival" class="custom-buttons">
                                            Survival Guide ({{ countFor(selected_location.id, 'survival') }})
                                        </v-btn>
                                        <v-btn value="pub_crawl" class="custom-buttons">
                                            Pub Crawl Guide ({{ countFor(selected_location.id, 'pub_crawl') }})
                                        </v-btn>
                                    </v-btn-toggle>
                                    <v-spacer></v-spacer>
                                    <v-btn dark class="custom-buttons" @click="openForm(null)">
                                        <v-icon left small>mdi-plus</v-icon> Add entry
                                    </v-btn>
                                </div>
                                <v-data-table :headers="headers" :items="guides" :loading="loading" disable-pagination
                                    hide-default-footer no-data-text="No entries for this location yet"
                                    style="height:540px;overflow-y:auto;">
                                    <template v-slot:item="{ item }">
                                        <tr style="cursor:inherit;background-color:transparent;">
                                            <td style="width:90px;">
                                                <v-img v-if="item.image" :src="imageUrl(item.image)" width="70" height="50"
                                                    contain class="my-1"></v-img>
                                            </td>
                                            <td v-if="selected_type === 'survival'">{{ item.category }}</td>
                                            <td><b>{{ item.title }}</b></td>
                                            <td>{{ item.address }}</td>
                                            <td>{{ item.telephone }}</td>
                                            <td style="max-width:320px;">
                                                <div class="description-cell">{{ item.description }}</div>
                                            </td>
                                            <td style="white-space:nowrap;">
                                                <v-btn icon small @click="openForm(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
                                                <v-btn icon small color="red" @click="deleteGuide(item)"><v-icon small>mdi-delete</v-icon></v-btn>
                                            </td>
                                        </tr>
                                    </template>
                                </v-data-table>
                            </template>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn class="custom-buttons" text @click="closeDialog()">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="form_dialog" max-width="700" persistent>
            <v-card>
                <v-card-title>
                    {{ form.id ? 'Edit' : 'Add' }} {{ selected_type === 'survival' ? 'survival' : 'pub crawl' }} entry
                    <span v-if="selected_location" class="ml-2 grey--text text-body-2">({{ selected_location.name }})</span>
                </v-card-title>
                <v-card-text>
                    <v-combobox v-if="selected_type === 'survival'" v-model="form.category" :items="survival_categories"
                        label="Category" hint="e.g. Food, Taxi, Pharmacy, Doctor, Garda" persistent-hint outlined dense
                        class="mb-3"></v-combobox>
                    <v-text-field v-model="form.title" :label="selected_type === 'survival' ? 'Name *' : 'Pub name *'"
                        outlined dense></v-text-field>
                    <v-text-field v-model="form.address" label="Address" outlined dense></v-text-field>
                    <v-text-field v-model="form.telephone" label="Telephone" outlined dense></v-text-field>
                    <v-textarea v-model="form.description" label="Description" outlined dense rows="3" auto-grow></v-textarea>
                    <div class="d-flex align-center">
                        <v-img v-if="form.image && !form.remove_image && !form.image_file" :src="imageUrl(form.image)"
                            max-width="120" max-height="90" contain class="mr-4"></v-img>
                        <v-file-input v-model="form.image_file" accept="image/*" label="Image" outlined dense
                            prepend-icon="mdi-camera" show-size></v-file-input>
                    </div>
                    <v-checkbox v-if="form.image && !form.image_file" v-model="form.remove_image" label="Remove current image"
                        dense hide-details class="mt-0"></v-checkbox>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn class="custom-buttons" text @click="form_dialog = false">Cancel</v-btn>
                    <v-btn dark class="custom-buttons" :loading="saving" @click="saveGuide()">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { mapGetters } from 'vuex'
export default {
    props: [
        'show_location_guides_dialog'
    ],
    data: () => ({
        api_url: process.env.VUE_APP_API_URL,
        locations: [],
        counts: [],
        location_search: null,
        selected_location_id: null,
        selected_type: 'survival',
        guides: [],
        loading: false,
        survival_categories: ['Food', 'Fast Food', 'Taxi', 'Pharmacy', 'Doctor', 'Garda'],
        form_dialog: false,
        saving: false,
        form: {},
    }),
    computed: {
        ...mapGetters({
            get_selected_company: 'auth/get_selected_company',
        }),
        filtered_locations() {
            const search = (this.location_search || '').toLowerCase()
            const locations = this.locations.filter(location => location.name.toLowerCase().includes(search))
            // Locations with guides first, then A-Z.
            return locations.sort((a, b) => {
                const a_has = this.countFor(a.id, 'survival') + this.countFor(a.id, 'pub_crawl') > 0 ? 0 : 1
                const b_has = this.countFor(b.id, 'survival') + this.countFor(b.id, 'pub_crawl') > 0 ? 0 : 1
                return a_has - b_has || a.name.localeCompare(b.name)
            })
        },
        selected_location() {
            return this.locations.find(location => location.id === this.selected_location_id) || null
        },
        headers() {
            const headers = [{ text: '', sortable: false }]
            if (this.selected_type === 'survival') {
                headers.push({ text: 'Category', sortable: false })
            }
            return headers.concat([
                { text: 'Name', sortable: false },
                { text: 'Address', sortable: false },
                { text: 'Telephone', sortable: false },
                { text: 'Description', sortable: false },
                { text: '', sortable: false },
            ])
        },
    },
    methods: {
        async load() {
            const [locations, counts] = await Promise.all([
                this.$axios.get(`admin/locations/get_locations/${this.get_selected_company.id}`),
                this.$axios.get(`admin/location_guides/get_guide_counts/${this.get_selected_company.id}`),
            ])
            if (locations.data.response) {
                this.locations = locations.data.data
            }
            if (counts.data.response) {
                this.counts = counts.data.data
            }
            if (!this.selected_location_id && this.filtered_locations.length) {
                this.selected_location_id = this.filtered_locations[0].id
            }
        },
        async refreshCounts() {
            const response = await this.$axios.get(`admin/location_guides/get_guide_counts/${this.get_selected_company.id}`)
            if (response.data.response) {
                this.counts = response.data.data
            }
        },
        async getGuides() {
            if (!this.selected_location_id) {
                this.guides = []
                return
            }
            this.loading = true
            try {
                const response = await this.$axios.get(`admin/location_guides/get_guides/${this.get_selected_company.id}`, {
                    params: { location_id: this.selected_location_id, type: this.selected_type }
                })
                if (response.data.response) {
                    this.guides = response.data.data
                }
            } finally {
                this.loading = false
            }
        },
        countFor(location_id, type) {
            const row = this.counts.find(count => count.location_id === location_id && count.type === type)
            return row ? row.total : 0
        },
        imageUrl(image) {
            return `${this.api_url}media/guides/${image}`
        },
        openForm(item) {
            this.form = item
                ? { ...item, image_file: null, remove_image: false }
                : { id: null, category: null, title: '', address: '', telephone: '', description: '', image: null, image_file: null, remove_image: false }
            this.form_dialog = true
        },
        async saveGuide() {
            if (!this.form.title || !this.form.title.trim()) {
                this.$toast.error('Please enter a name')
                return
            }
            const formData = new FormData()
            formData.append('company_id', this.get_selected_company.id)
            formData.append('location_id', this.selected_location_id)
            formData.append('type', this.selected_type)
            for (const field of ['category', 'title', 'address', 'telephone', 'description']) {
                formData.append(field, this.form[field] || '')
            }
            if (this.form.image_file) {
                formData.append('image', this.form.image_file)
            }
            if (this.form.remove_image) {
                formData.append('remove_image', 1)
            }

            this.saving = true
            try {
                const url = this.form.id
                    ? `admin/location_guides/update_guide/${this.form.id}`
                    : 'admin/location_guides/create_guide'
                const { data } = await this.$axios.post(url, formData)
                if (data.response) {
                    this.$toast.success(data.message)
                    this.form_dialog = false
                    this.getGuides()
                    this.refreshCounts()
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Could not save the entry')
            } finally {
                this.saving = false
            }
        },
        async deleteGuide(item) {
            if (!confirm(`Delete "${item.title}"?`)) {
                return
            }
            const { data } = await this.$axios.delete(`admin/location_guides/delete_guide/${item.id}`)
            if (data.response) {
                this.$toast.success(data.message)
                this.getGuides()
                this.refreshCounts()
            }
        },
        closeDialog() {
            this.$emit('closeLocationGuidesDialog')
        },
    },
    watch: {
        show_location_guides_dialog(open) {
            if (open) {
                this.load()
            }
        },
        selected_location_id() {
            this.getGuides()
        },
        selected_type() {
            this.getGuides()
        },
    }
}
</script>

<style scoped lang="scss">
:deep(.custom-buttons span) {
    text-transform: none;
    font-family: Manrope;
    font-size: 13px !important;
    font-style: normal;
    font-weight: 600 !important;
    line-height: normal;
    box-shadow: none !important;
}

.description-cell {
    font-size: 12px;
    color: #555;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
